import { useState, useRef } from "react";
import api from "@/lib/api";
import { fileToResizedBase64 } from "@/lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

// Two-sided business-card scanner (recto + optional verso) → OCR → onScanned(contactInfo)
export default function CardScanDialog({ open, onClose, onScanned, onOffline }) {
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [scanning, setScanning] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const reset = () => { setFront(null); setBack(null); setScanning(false); };
  const close = () => { reset(); onClose(); };

  const pick = async (e, side) => {
    const file = e.target.files?.[0];
    const ref = side === "front" ? frontRef : backRef;
    if (ref.current) ref.current.value = "";
    if (!file) return;
    if (!navigator.onLine) { onOffline?.(file); close(); return; }
    try {
      const b64 = await fileToResizedBase64(file);
      const url = URL.createObjectURL(file);
      if (side === "front") setFront({ b64, url });
      else setBack({ b64, url });
    } catch { toast.error("Image illisible, réessayez."); }
  };

  const analyze = async () => {
    if (!front) { toast.error("Photographiez d'abord le recto."); return; }
    setScanning(true);
    try {
      const { data } = await api.post("/scan-card", { imageBase64: front.b64, imageBase64Back: back?.b64 || "" });
      toast.success(back ? "Recto + verso analysés ✨" : "Carte analysée ✨");
      const ci = data.contactInfo;
      close();
      onScanned(ci);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'analyse OCR");
      setScanning(false);
    }
  };

  const tiles = [
    { side: "front", label: "Recto", state: front, ref: frontRef },
    { side: "back", label: "Verso (optionnel)", state: back, ref: backRef },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="card-scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Scanner une carte de visite</DialogTitle>
          <DialogDescription>Photographiez le recto — ajoutez le verso si la carte a des infos au dos (adresse, etc.).</DialogDescription>
        </DialogHeader>
        <input ref={frontRef} type="file" accept="image/*" capture="environment" onChange={(e) => pick(e, "front")} className="hidden" data-testid="scan-front-input" />
        <input ref={backRef} type="file" accept="image/*" capture="environment" onChange={(e) => pick(e, "back")} className="hidden" data-testid="scan-back-input" />
        <div className="grid grid-cols-2 gap-3">
          {tiles.map((t) => (
            <button
              key={t.side}
              type="button"
              disabled={scanning}
              onClick={() => t.ref.current?.click()}
              data-testid={`scan-${t.side}-tile`}
              className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 aspect-[3/2] flex flex-col items-center justify-center hover:bg-primary/10 transition-colors disabled:opacity-60"
            >
              {t.state ? (
                <>
                  <img src={t.state.url} alt={t.label} className="absolute inset-0 w-full h-full object-cover" />
                  <span className="absolute inset-0 bg-black/40" />
                  <Check className="w-7 h-7 text-white relative" />
                  <span className="text-[11px] font-semibold text-white relative mt-1">{t.label} ✓ · reprendre</span>
                </>
              ) : (
                <>
                  <Camera className="w-7 h-7 text-primary" />
                  <span className="text-xs font-semibold mt-1.5">{t.label}</span>
                </>
              )}
              {scanning && t.state && <span className="klt-scanline" />}
            </button>
          ))}
        </div>
        <Button onClick={analyze} disabled={!front || scanning} className="w-full rounded-xl" data-testid="scan-analyze-button">
          {scanning ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse OCR en cours…</>) : (<>Analyser la carte</>)}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
